import React, { useRef, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const GalaxyField: React.FC<{ count?: number }> = ({ count = 3000 }) => {
    const pointsRef = useRef<THREE.Points>(null!);
    const baseSize = useMemo(() => 1.2, []);

    // Spiral-Positionen berechnen
    const { positions, colors, angles, radii } = useMemo(() => {
        const positions = new Float32Array(count * 3);
        const colors = new Float32Array(count * 3);
        const angles: number[] = [];
        const radii: number[] = [];

        for (let i = 0; i < count; i++) {
            const radius = Math.random() * 20;
            const angle = radius * 1.5 + (Math.random() - 0.5) * 3; // Spiral + Zufall
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            const z = (Math.random() - 0.5) * 2;

            positions.set([x, y, z], i * 3);
            radii.push(radius);
            angles.push(angle);

            // Farbverlauf: innen hell, außen bläulich
            const intensity = 1 - radius / 6;
            colors.set([1.0, 0.8 * intensity, intensity], i * 3);
        }

        return { positions, colors, angles, radii };
    }, [count]);

    // Animation: Winkel über Zeit erhöhen
    useFrame(({ clock }) => {
        const t = clock.getElapsedTime();
        const geo = pointsRef.current.geometry;
        const posAttr = geo.getAttribute('position') as THREE.BufferAttribute;

        for (let i = 0; i < count; i++) {
            const baseAngle = angles[i];
            const radius = radii[i];

            const angle = baseAngle + t * 0.1 * (0.2 + radius * 0.02);
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            const z = posAttr.array[i * 3 + 2]; // Z bleibt

            posAttr.array[i * 3 + 0] = x;
            posAttr.array[i * 3 + 1] = y;
            posAttr.array[i * 3 + 2] = z;
        }

        posAttr.needsUpdate = true;
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute attach="attributes-position" args={[positions, 3]} />
                <bufferAttribute attach="attributes-color" args={[colors, 3]} />
            </bufferGeometry>
            <pointsMaterial
                vertexColors
                size={baseSize}
                sizeAttenuation={true}
                transparent
                opacity={1}
                blending={THREE.AdditiveBlending}
                depthWrite={false}
            />
        </points>
    );
};

export default GalaxyField;